import { useCallback, useEffect, useState } from 'react';

import { rulesApi } from './api.js';
import { DraftError, fromDraft, move, toDraft, type ResponseDraft } from './draft.js';
import { ResponseFields } from './ResponseFields.jsx';
import { RuleForm, type RuleDraftValue } from './RuleForm.jsx';
import { RuleList } from './RuleList.jsx';
import { RuleTester } from './RuleTester.jsx';
import type { Endpoint, Rule } from './types.js';

const STORAGE_KEY = 'mock-api:rules-editor';

interface Credentials {
  slug: string;
  token: string;
}

/**
 * There are no accounts, so the token *is* the session. Keeping it in
 * `localStorage` means a reload does not lock you out of an endpoint you just
 * created; it never leaves this origin, and "Salir" wipes it.
 */
function loadCredentials(): Credentials | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as Partial<Credentials>;
    return parsed.slug && parsed.token ? { slug: parsed.slug, token: parsed.token } : null;
  } catch {
    return null;
  }
}

function storeCredentials(credentials: Credentials | null): void {
  try {
    if (credentials) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(credentials));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // A browser with storage disabled still gets a working editor for this tab.
  }
}

const INPUT =
  'rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100 ' +
  'placeholder:text-slate-600 focus:border-sky-500 focus:outline-none';

export interface RulesEditorProps {
  /**
   * Credentials the caller already has. The inspector passes the endpoint it is
   * watching, so opening its rules costs nothing: no second slug to type, no
   * second token to paste. Left out, the editor asks for them itself and keeps
   * them under its own key — which is how it is reached standalone.
   */
  credentials?: Credentials | undefined;
}

/**
 * Rules editor for one endpoint: its rules, its default response and the
 * dry-run tester. Holds no global state and mounts anywhere.
 */
export function RulesEditor({ credentials: provided }: RulesEditorProps = {}) {
  const [own, setOwn] = useState<Credentials | null>(loadCredentials);
  const credentials = provided ?? own;

  if (!credentials) {
    return (
      <ConnectForm
        onConnect={(next) => {
          storeCredentials(next);
          setOwn(next);
        }}
      />
    );
  }

  return (
    <ConnectedEditor
      // Remounting on a credential change is the whole reset: no stale rules,
      // no stale endpoint, no effect racing the previous slug's response.
      key={`${credentials.slug}:${credentials.token}`}
      credentials={credentials}
      // Whoever owns the session owns the way out of it. When the inspector
      // supplies the endpoint, leaving it is its "← Inicio", not a second
      // "Salir" that would drop a session this editor does not own.
      {...(provided
        ? {}
        : {
            onDisconnect: () => {
              storeCredentials(null);
              setOwn(null);
            },
          })}
    />
  );
}

function ConnectForm({ onConnect }: { onConnect: (credentials: Credentials) => void }) {
  const [slug, setSlug] = useState('');
  const [token, setToken] = useState('');

  return (
    <form
      className="grid gap-3 rounded-xl border border-slate-800 bg-slate-900/60 p-5"
      data-testid="connect-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (slug.trim() && token.trim()) onConnect({ slug: slug.trim(), token: token.trim() });
      }}
    >
      <div>
        <h2 className="text-lg font-semibold text-slate-100">Reglas de respuesta</h2>
        <p className="mt-1 text-sm text-slate-400">
          Abre un endpoint con su slug y su token para configurar qué contesta y en qué ruta.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          aria-label="Slug del endpoint"
          className={`${INPUT} w-40 font-mono`}
          placeholder="7fk2mqxa"
          value={slug}
          onChange={(event) => setSlug(event.target.value)}
        />
        <input
          aria-label="Token del endpoint"
          className={`${INPUT} flex-1 font-mono`}
          placeholder="token"
          type="password"
          value={token}
          onChange={(event) => setToken(event.target.value)}
        />
        <button
          type="submit"
          className="rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-500"
        >
          Abrir
        </button>
      </div>
    </form>
  );
}

interface ConnectedEditorProps {
  credentials: Credentials;
  /** Absent when the caller owns the session; then there is nothing to exit. */
  onDisconnect?: (() => void) | undefined;
}

function ConnectedEditor({ credentials, onDisconnect }: ConnectedEditorProps) {
  const { slug, token } = credentials;

  const [endpoint, setEndpoint] = useState<Endpoint | null>(null);
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Rule | 'new' | null>(null);
  const [highlighted, setHighlighted] = useState<string | null>(null);

  const report = useCallback((thrown: unknown): void => {
    setError(thrown instanceof Error ? thrown.message : 'Algo ha fallado.');
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    const [loaded, listed] = await Promise.all([
      rulesApi.getEndpoint(slug, token),
      rulesApi.list(slug, token),
    ]);
    setEndpoint(loaded);
    setRules(listed.rules);
  }, [slug, token]);

  useEffect(() => {
    let live = true;

    refresh()
      .catch((thrown: unknown) => {
        if (live) report(thrown);
      })
      .finally(() => {
        if (live) setLoading(false);
      });

    return () => {
      live = false;
    };
  }, [refresh, report]);

  /** Every mutation ends the same way: re-read, so the screen is never a guess. */
  const run = async (action: () => Promise<unknown>): Promise<void> => {
    setError(null);
    try {
      await action();
      await refresh();
    } catch (thrown) {
      report(thrown);
      // Pull the truth back even after a failure: a rejected reorder must not
      // leave the list showing an order the server never accepted.
      await refresh().catch(() => {});
    }
  };

  const reorder = async (from: number, to: number): Promise<void> => {
    const next = move(rules, from, to);
    setRules(next);
    await run(() =>
      rulesApi.reorder(
        slug,
        token,
        next.map((rule) => rule.id),
      ),
    );
  };

  const save = async (value: RuleDraftValue): Promise<void> => {
    const target = editing;
    await run(() =>
      target === 'new' || target === null
        ? rulesApi.create(slug, token, { ...value, enabled: true })
        : rulesApi.update(slug, token, target.id, value),
    );
    setEditing(null);
  };

  if (loading) {
    return <p className="text-sm text-slate-400">Cargando reglas…</p>;
  }

  return (
    <section className="grid gap-5" data-testid="rules-editor">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-slate-100">
            Reglas de <code className="font-mono text-sky-300">{slug}</code>
          </h2>
          {endpoint ? (
            <p className="text-xs text-slate-500">
              {endpoint.url} · {endpoint.requestCount} requests
            </p>
          ) : null}
        </div>

        {onDisconnect ? (
          <button
            type="button"
            onClick={onDisconnect}
            className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-400 hover:border-slate-500"
          >
            Salir
          </button>
        ) : null}
      </header>

      {error ? (
        <p
          className="rounded-md border border-rose-900 bg-rose-950/40 px-3 py-2 text-sm text-rose-300"
          role="alert"
          data-testid="editor-error"
        >
          {error}
        </p>
      ) : null}

      <div className="grid gap-3">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-slate-200">
            Reglas <span className="font-normal text-slate-500">· gana la primera que casa</span>
          </h3>
          {editing === null ? (
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="rounded-md bg-sky-600 px-3 py-1 text-sm font-medium text-white hover:bg-sky-500"
            >
              Nueva regla
            </button>
          ) : null}
        </div>

        <RuleList
          rules={rules}
          highlightedId={highlighted}
          onReorder={(from, to) => void reorder(from, to)}
          onToggle={(rule) =>
            void run(() => rulesApi.update(slug, token, rule.id, { enabled: !rule.enabled }))
          }
          onEdit={setEditing}
          onDelete={(rule) => void run(() => rulesApi.remove(slug, token, rule.id))}
        />

        {editing !== null ? (
          <RuleForm
            {...(editing === 'new' ? {} : { rule: editing })}
            onSubmit={save}
            onCancel={() => setEditing(null)}
          />
        ) : null}
      </div>

      <RuleTester
        onTest={(probe) => rulesApi.test(slug, token, probe)}
        onResult={(result) => setHighlighted(result?.ruleId ?? null)}
      />

      {endpoint ? (
        <DefaultResponseEditor
          endpoint={endpoint}
          onSave={(defaultResponse) =>
            run(() => rulesApi.updateEndpoint(slug, token, { defaultResponse }))
          }
        />
      ) : null}
    </section>
  );
}

interface DefaultResponseEditorProps {
  endpoint: Endpoint;
  onSave: (response: ReturnType<typeof fromDraft>) => Promise<void>;
}

/** What the endpoint answers when no rule matches. */
function DefaultResponseEditor({ endpoint, onSave }: DefaultResponseEditorProps) {
  const [draft, setDraft] = useState<ResponseDraft>(() => toDraft(endpoint.defaultResponse));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (): Promise<void> => {
    setError(null);

    try {
      const response = fromDraft(draft);
      // The endpoint's default has nowhere to put a delay; only rules stall.
      delete response.delayMs;

      setSaving(true);
      await onSave(response);
    } catch (thrown) {
      setError(thrown instanceof DraftError ? thrown.message : 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="grid gap-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4"
      data-testid="default-response-form"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <h3 className="text-sm font-semibold text-slate-200">
        Respuesta por defecto{' '}
        <span className="font-normal text-slate-500">· cuando no casa ninguna regla</span>
      </h3>

      <ResponseFields draft={draft} onChange={setDraft} idPrefix="default" withDelay={false} />

      {error ? (
        <p className="text-sm text-rose-400" role="alert">
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={saving}
        className="justify-self-start rounded-md border border-sky-700 px-3 py-1 text-sm text-sky-300 hover:bg-sky-900/40 disabled:opacity-50"
      >
        {saving ? 'Guardando…' : 'Guardar respuesta por defecto'}
      </button>
    </form>
  );
}
